import React, { useEffect, useState } from 'react';
import { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { router } from '@forge/bridge';

import issueApi from '../../api/issueApi';
import serverApi from '../../api/serverApi';
import { getJqlTextForMatchingOptionToDelete } from '../../api/utils';
import fieldForgeApi from '../../bridge/fieldForgeApi';
import { ErrorResponseObject } from '../../interfaces';
import {
    IDS_KEY,
    IS_NOT_EMPTY,
    ISSUES,
    MAX_NESTING_LEVEL,
    VALUE_LEVEL,
    VALUE_LEVEL_1_KEY,
    VALUES_KEY,
} from '../../utils/constants';
import { fetchIssuesWithMatchingOptionValue, getCustomFieldId, logResponseError } from '../../utils/levelModelUtils';

import { ABSOLUTE } from './constants';
import {
    CustomFieldValue,
    DeleteOptionModalProperties as Properties,
    IssueItem,
    IssueNormalized,
    IssuesWithFieldValueSection as IssuesFoundProperties,
    ParentOptionData,
} from './interfaces';
import { PositionedElement } from './styles';

const renderBodyText = () => (
    <div>
        <p>Confirm that you want to delete this option.</p>
        <p>
            Deleting a custom field option <strong>removes all matching values from all issues</strong>.
        </p>
        <p>
            The all related options that are configured as <strong>children</strong> for this option{' '}
            <strong>will also be deleted</strong> - from configuration and from issues.
        </p>
        <p>If you don&apos;t want to lose the value of issues, we recommend disabling the options.</p>
    </div>
);

const getIssueNavigatorUrl = (
    customFieldId: string,
    baseUrl: string,
    optionValue: string,
    levelToCheck: number,
    fieldName: string,
    levelParents: ParentOptionData[]
) =>
    `${baseUrl}/${ISSUES}/?jql=cf[${customFieldId}] ${IS_NOT_EMPTY} ${getJqlTextForMatchingOptionToDelete(levelParents, optionValue, levelToCheck, fieldName)}`;

const IssuesWithFieldValueSection: React.FC<IssuesFoundProperties> = ({ issuesFound, url }) => (
    <p>
        Issues with this value: &nbsp;<strong>{issuesFound}</strong>
        &nbsp;
        {issuesFound > 0 && (
            <>
                &#40;<a onClick={() => router.open(url)}>find them in the Issue Navigator</a>&#41;
            </>
        )}
    </p>
);

const normalizeIssues = (issues: IssueItem[], customField: string): IssueNormalized[] =>
    issues.map((issue: IssueItem) => ({
        id: issue.id,
        key: issue.key,
        customFieldValue: issue.fields[customField],
    }));

const getIssuesWithMatchingOptionValue = (issues: IssueNormalized[], optionValue: string): IssueNormalized[] =>
    issues.filter((issue: IssueNormalized) => {
        const { customFieldValue } = issue;

        return Object.values(customFieldValue).some(
            (value: string | string[]) => typeof value === 'string' && value.includes(optionValue)
        );
    });

const optionIsInIdsArray = (value: CustomFieldValue, key: string, optionKey: string): boolean =>
    key === IDS_KEY && Array.isArray(value[key]) && value[key].includes(optionKey);

const optionIsSelectedOnSomeLevel = (value: CustomFieldValue, key: string, optionValue: string): boolean =>
    typeof value[key] === 'string' && value[key] === optionValue;

const optionIsInValuesString = (value: CustomFieldValue, key: string, optionValue: string): boolean =>
    typeof value[key] === 'string' && value[key].includes(optionValue);

const removeOptionFromField = (
    value: CustomFieldValue,
    optionValue: string,
    optionKey: string,
    indexOptionIsAt: number
): CustomFieldValue => {
    const keysToDelete: string[] = [];

    Object.keys(value).forEach((key: string) => {
        if (optionIsInIdsArray(value, key, optionKey)) {
            // @ts-ignore
            value[key] = value[key].filter((keyItem: string) => keyItem !== optionKey);
        } else if (optionIsSelectedOnSomeLevel(value, key, optionValue)) {
            keysToDelete.push(key);
        } else if (optionIsInValuesString(value, key, optionValue)) {
            const keyValue = value[key];
            const splittedKeyValue = keyValue.split(',');
            const withValueRemoved = splittedKeyValue
                .filter((item: string, index: number) => !(item.trim() === optionValue && index === indexOptionIsAt))
                .map((item: string) => item.trim());
            value[key] = withValueRemoved.join(', ');
        }
    });

    if (keysToDelete.length === 1 && keysToDelete[0] === VALUE_LEVEL_1_KEY) {
        value[VALUE_LEVEL_1_KEY] = '';
    }

    for (let i = indexOptionIsAt + 1; i <= MAX_NESTING_LEVEL; i++) {
        delete value[`${VALUE_LEVEL}${i}`];
    }

    value[IDS_KEY] = value.ids.slice(0, indexOptionIsAt);
    const splittedValues = value[VALUES_KEY].split(', ');
    const withValuesRemoved = splittedValues.slice(0, indexOptionIsAt);
    value[VALUES_KEY] = withValuesRemoved.join(', ');

    return value;
};

const removeOptionFromIssue = async (
    issue: IssueNormalized,
    optionValue: string,
    optionKey: string,
    customField: string,
    indexOptionIsAt: number
) => {
    const { customFieldValue } = issue;
    removeOptionFromField(customFieldValue, optionValue, optionKey, indexOptionIsAt);
    const body = {
        fields: {
            [customField]: customFieldValue,
        },
    };
    await issueApi.updateIssue(JSON.stringify(body), issue.key);
};

const DeleteOptionModal: React.FC<Properties> = ({
    optionValue,
    optionKey,
    customField,
    onClose,
    onSubmit,
    indexOptionIsAt,
    levelParents,
    setIsOptionDeletionInProgress,
}) => {
    const [isFetching, setIsFetching] = useState(true);
    const [issuesWithMatchingOptionValue, setIssuesWithMatchingOptionValue] = useState<IssueNormalized[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [updatedIssueCounter, setUpdatedIssueCounter] = useState(0);
    const [baseUrl, setBaseUrl] = useState('');
    const [fieldName, setFieldName] = useState('');

    const fetchData = async () => {
        try {
            const startAt = 0;
            const fetchedData: IssueNormalized[] = [];
            const baseUrl = await serverApi.getBaseUrl();
            const fields = await fieldForgeApi.getFields();
            const field = fields.find((fieldValue: any) => fieldValue.id === customField);

            await fetchIssuesWithMatchingOptionValue(
                startAt,
                fetchedData,
                // eslint-disable-next-line @typescript-eslint/unbound-method
                issueApi.getIssuesWithMatchingOptionValue,
                normalizeIssues,
                customField,
                field?.name as string,
                indexOptionIsAt + 1,
                optionValue,
                levelParents
            );
            const issuesWithMatchingOptionValues = getIssuesWithMatchingOptionValue(fetchedData, optionValue);

            setBaseUrl(baseUrl);
            setFieldName(field?.name as string);
            setIssuesWithMatchingOptionValue(issuesWithMatchingOptionValues);
        } catch (error) {
            logResponseError(error as ErrorResponseObject);
        } finally {
            setIsFetching(false);
        }
    };

    const handleDeletion = async () => {
        setIsSaving(true);

        for (const issue of issuesWithMatchingOptionValue) {
            await removeOptionFromIssue(issue, optionValue, optionKey, customField, indexOptionIsAt);
            setUpdatedIssueCounter(prevState => prevState + 1);
        }

        onSubmit();
        onClose();
        setIsSaving(false);
    };

    const handleModalClosing = () => {
        onClose();
        setIsOptionDeletionInProgress(false);
    };

    useEffect(() => {
        fetchData().then(() => {
            setIsFetching(false);
        });
    }, []);

    return (
        <Modal
            onClose={() => !isSaving && handleModalClosing()}
            autoFocus={false}
        >
            <ModalHeader>
                <ModalTitle appearance='danger'>Delete option</ModalTitle>
            </ModalHeader>
            <div>
                <ModalBody>
                    {renderBodyText()}
                    {isFetching ?
                        <PositionedElement position={ABSOLUTE}>
                            <Spinner size='medium' />
                        </PositionedElement>
                    :   <IssuesWithFieldValueSection
                            issuesFound={issuesWithMatchingOptionValue.length}
                            url={getIssueNavigatorUrl(
                                getCustomFieldId(customField),
                                baseUrl,
                                optionValue,
                                indexOptionIsAt + 1,
                                fieldName,
                                levelParents
                            )}
                        />
                    }
                    {isSaving && (
                        <p>
                            Issues updated so far: <strong>{updatedIssueCounter}</strong>
                        </p>
                    )}
                </ModalBody>
            </div>
            <ModalFooter>
                <ButtonGroup>
                    <LoadingButton
                        onClick={handleModalClosing}
                        appearance='subtle'
                        isLoading={isSaving}
                    >
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        appearance='danger'
                        onClick={handleDeletion}
                        isLoading={isFetching || isSaving}
                    >
                        Delete
                    </LoadingButton>
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    );
};

export default DeleteOptionModal;
